/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CommunicationListItem } from '@gh/builder-shared';
import {
  createOrderIndexDriver,
  createTableDriver,
  FormStateSnapshot,
  getFormStateSnapshotValue,
  isDirty,
  isDirtyTypeNew,
  Table,
} from '@gh/common-shared';
import { DirtyType, EntityVersion, ID, WithDirtyType, WithVersion } from '@gh/common-shared-data';
import { FormMode, isCreateFormMode } from '@gh/core-ui';
import { composeComparators, isNotNil, negateComparator, valueComparator } from '@gh/core-util';
import { constant, first, isNil, pick, stubTrue } from 'lodash';
import {
  getLeadSalesFolderId,
  getLeadSalesFolderMeetingId,
  LeadContactDetails,
  LeadDetailsInit,
  LeadDetailsSave,
  LeadDetailsSaveResponse,
  LeadMasterInfo,
  LeadSalesFolder,
  LeadSalesFolderComplete,
  LeadSfJobDetails,
  validateLeadMasterInfo,
  validatesLeadSalesFolder,
} from '../../resources/lead.model';

export enum LeadDetailsStateEventType {
  Load, ResetDirty,
  TabSelected,
  SalesFolderAdd, SalesFolderEdit, SalesFolderReset, SalesFolderChange, SalesFolderLoad,
  CurrentSalesFolderSetGeneral, CurrentSalesFolderSetMemo,
  CommitChanges, SetCurrentSalesFolder,
  SetMasterInfo, ContactSelect,
  NavigateToInvalid, ChangeCommunicationListFilter,
  CommunicationListItemAdd,
  CommunicationListItemUpdate,
  CurrentSalesFolderSetGeneralInit,
}

export type LeadDetailsStateLoadEvent = {
  type: LeadDetailsStateEventType.Load;
  details: LeadDetailsInit;
  mode: FormMode;
};
export type LeadDetailsStateTabSelectedEvent = { type: LeadDetailsStateEventType.TabSelected; tab: LeadDetailsTabName };
export type LeadDetailsStateSfAddEvent = { type: LeadDetailsStateEventType.SalesFolderAdd };
export type LeadDetailsStateSfEditEvent = { type: LeadDetailsStateEventType.SalesFolderEdit };
export type LeadDetailsStateSfResetEvent = { type: LeadDetailsStateEventType.SalesFolderReset };
export type LeadDetailsStateSfChangeEvent = { type: LeadDetailsStateEventType.SalesFolderChange; folderId: ID };
export type LeadDetailsStateChangeCommunicationListFilterEvent = {
  type: LeadDetailsStateEventType.ChangeCommunicationListFilter;
  communicationListFilters: LeadCommunicationListFilters;
};
export type LeadDetailsStateCommunicationListAddEvent = {
  type: LeadDetailsStateEventType.CommunicationListItemAdd;
  communicationListItem: CommunicationListItem;
};
export type LeadDetailsStateCommunicationListUpdateEvent = {
  type: LeadDetailsStateEventType.CommunicationListItemUpdate;
  communicationListItem: CommunicationListItem;
};
export type LeadDetailsStateCurrentSfSetMemoEvent = {
  type: LeadDetailsStateEventType.CurrentSalesFolderSetMemo;
  memo: string;
};
export type LeadDetailsStateSfLoadEvent = {
  type: LeadDetailsStateEventType.SalesFolderLoad;
  salesFolder: LeadSalesFolderComplete;
};
export type LeadDetailsStateCurrentSfSetGeneralEvent = {
  type: LeadDetailsStateEventType.CurrentSalesFolderSetGeneral;
  snapshot: FormStateSnapshot<LeadSalesFolder>;
};
export type LeadDetailsStateCurrentSfSetGeneralInitEvent = {
  type: LeadDetailsStateEventType.CurrentSalesFolderSetGeneralInit;
  snapshot: FormStateSnapshot<LeadSalesFolder>;
};

export type LeadDetailsStateCommitChangesEvent = {
  type: LeadDetailsStateEventType.CommitChanges;
  saveResponse: LeadDetailsSaveResponse;
};

export type LeadDetailsStateSetMasterInfoEvent = {
  type: LeadDetailsStateEventType.SetMasterInfo;
  masterInfo: FormStateSnapshot<LeadMasterInfo>;
};

export type LeadDetailsStateContactSelectEvent = {
  type: LeadDetailsStateEventType.ContactSelect;
  contact?: LeadContactDetails;
};

export type LeadDetailsStateResetDirtyEvent = { type: LeadDetailsStateEventType.ResetDirty };

export type LeadDetailsStateNavigateToInvalidEvent = { type: LeadDetailsStateEventType.NavigateToInvalid };

export type LeadDetailsStateEvent = LeadDetailsStateLoadEvent
  | LeadDetailsStateTabSelectedEvent
  | LeadDetailsStateSfAddEvent | LeadDetailsStateSfEditEvent | LeadDetailsStateSfResetEvent
  | LeadDetailsStateSfChangeEvent | LeadDetailsStateSfLoadEvent | LeadDetailsStateCurrentSfSetMemoEvent
  | LeadDetailsStateCurrentSfSetGeneralEvent
  | LeadDetailsStateCurrentSfSetGeneralInitEvent
  | LeadDetailsStateCommitChangesEvent
  | LeadDetailsStateSetMasterInfoEvent | LeadDetailsStateContactSelectEvent
  | LeadDetailsStateNavigateToInvalidEvent
  | LeadDetailsStateResetDirtyEvent
  | LeadDetailsStateChangeCommunicationListFilterEvent
  | LeadDetailsStateCommunicationListAddEvent
  | LeadDetailsStateCommunicationListUpdateEvent;

export enum LeadDetailsTabName {
  DetailsAndCommunications = 'DETAILS_AND_COMMUNICATIONS',
  SalesFolders = 'SALES_FOLDERS',
}

export type LeadDetailsSelection = {
  tab: LeadDetailsTabName;
};

export type CurrentSalesFolder = WithDirtyType<{
  id: ID;
  general: FormStateSnapshot<LeadSalesFolder>;
  memo: string;
  jobDetails?: LeadSfJobDetails;
  version?: EntityVersion;
  isDirty: boolean;
}>;

export type LeadDetailsSettings = {
  firstContactLetterPromptPrint: boolean;
  isDppJobAllowed: boolean;
};

export type LeadCommunicationListFilters = {
  showSms: boolean;
  showEmail: boolean;
  showDoc: boolean;
  showActivity: boolean;
  showWebsite: boolean;
};

export type LeadDetailsState = WithVersion<{
  formMode: FormMode;
  masterInfo: FormStateSnapshot<LeadMasterInfo>;
  email?: string;
  salesFolders: Table<LeadSalesFolder>;
  currentSalesFolder?: CurrentSalesFolder;
  currentSalesFolderInEditing: boolean;
  isDirty: boolean;
  isUnsubscribed: boolean;
  selection: LeadDetailsSelection;
  communications: CommunicationListItem[];
  settings: LeadDetailsSettings;
  communicationListFilters: LeadCommunicationListFilters;
  gDriveUrl: string;
  gDriveFolderId: ID;
  activeSalesFolderId?: ID;
  salespersonId?: ID;
}>;

export const sfTable = createTableDriver(
  getLeadSalesFolderId,
  createOrderIndexDriver(
    'date',
    getLeadSalesFolderId,
    constant(true),
    composeComparators(negateComparator(valueComparator('created')), valueComparator(getLeadSalesFolderId))),
);

export const sfMeetingTable = createTableDriver(
  getLeadSalesFolderMeetingId,
  createOrderIndexDriver(
    'date',
    getLeadSalesFolderMeetingId,
    stubTrue,
    composeComparators(valueComparator('date'), valueComparator(getLeadSalesFolderMeetingId))),
);

export const LEAD_DETAILS_INIT_STATE: LeadDetailsState = {
  formMode: FormMode.CreateMode,
  masterInfo: <any>{},
  salesFolders: sfTable.create(),
  currentSalesFolderInEditing: false,
  email: '',
  selection: {
    tab: LeadDetailsTabName.DetailsAndCommunications,
  },
  settings: {
    firstContactLetterPromptPrint: false,
    isDppJobAllowed: false,
  },
  isDirty: false,
  gDriveUrl: '',
  isUnsubscribed: false,
  gDriveFolderId: '',
  communications: [],
  communicationListFilters: { showSms: true, showEmail: true, showDoc: true, showWebsite: true, showActivity: true },
};

export const buildSalesFolder = (sf: CurrentSalesFolder) => ({
  ...getFormStateSnapshotValue(sf.general),
  salesFolderId: sf.id,
  memo: sf.memo,
  isDirty: false,
});

export const queryLeadSalesFolderDirty = ({ currentSalesFolder }: LeadDetailsState) =>
  currentSalesFolder && currentSalesFolder.isDirty || false;
export const queryLeadDirty = (state: LeadDetailsState) => state.isDirty || queryLeadSalesFolderDirty(state);
export const queryLeadSelectedTab = (state: LeadDetailsState) => state.selection.tab;
export const queryLeadContact = ({ masterInfo }: LeadDetailsState) => getFormStateSnapshotValue(masterInfo).customer;
export const queryLeadActiveSalesFolderId = (state: LeadDetailsState) => state;
export const queryLeadId = (state: LeadDetailsState) => {
  const customer = queryLeadContact(state);
  return customer ? customer.contactId : void 0;
};
export const queryLeadContactName = (state: LeadDetailsState) => {
  const customer = queryLeadContact(state);
  return customer ? customer.name : void 0;
};
export const queryLeadCurrentSalesFolder = ({ currentSalesFolder }: LeadDetailsState) => currentSalesFolder;
export const queryLeadCurrentSalesFolderStatus = ({ currentSalesFolder }: LeadDetailsState) => {
  const sfGeneral = currentSalesFolder ? getFormStateSnapshotValue(currentSalesFolder.general) : void 0;

  return sfGeneral ? sfGeneral.status : void 0;
};
export const queryLeadIsCurrentSalesFolderNew = (state: LeadDetailsState) => {
  const sf = queryLeadCurrentSalesFolder(state);
  return sf && isDirtyTypeNew(sf.dirtyType) || false;
};
export const queryLeadSalesFolderInEditing = (state: LeadDetailsState) => state.currentSalesFolderInEditing;
export const queryLeadSalesFolders = (state: LeadDetailsState) => sfTable.byOrder(state.salesFolders, 'date');
export const queryLeadSalesFolder = (state: LeadDetailsState, id: ID) => sfTable.byId(state.salesFolders, id);
export const queryLeadFirstSalesFolder = (state: LeadDetailsState) => first(queryLeadSalesFolders(state));
export const queryLeadCurrentSalesFolderJobDetails = ({ currentSalesFolder }: LeadDetailsState) =>
  currentSalesFolder ? currentSalesFolder.jobDetails : void 0;
export const queryLeadCurrentSalesFolderMemo = ({ currentSalesFolder }: LeadDetailsState) =>
  currentSalesFolder && currentSalesFolder.memo || '';
export const queryLeadIsProduceFcl = ({ currentSalesFolder }: LeadDetailsState) =>
  currentSalesFolder ? currentSalesFolder.dirtyType === DirtyType.New : false;
export const queryLeadIsPromptFcl = ({ settings }: LeadDetailsState) => settings.firstContactLetterPromptPrint;
export const queryLeadIsDppAllowed = ({ settings }: LeadDetailsState) => settings.isDppJobAllowed;
export const queryLeadIsSalesFolderSelected = (state: LeadDetailsState) => !isNil(state.currentSalesFolder);
export const queryLeadSaveModel = (state: LeadDetailsState) => {
  const { currentSalesFolder } = state;
  const masterInfo = getFormStateSnapshotValue(state.masterInfo);
  const sfGeneral = currentSalesFolder ? getFormStateSnapshotValue(currentSalesFolder.general) : void 0;

  return <LeadDetailsSave>{
    dirtyType: isCreateFormMode(state.formMode) ? DirtyType.New : DirtyType.Updated,
    customer: masterInfo.customer,
    salesPerson: masterInfo.salesPerson,
    customerType: masterInfo.customerType,
    salesFolder: currentSalesFolder && sfGeneral && isDirty(currentSalesFolder) ? {
      ...pick(sfGeneral, ['priority', 'stage', 'status', 'notProceedingReason', 'name']),
      salesFolderId: currentSalesFolder.dirtyType === DirtyType.New ? void 0 : currentSalesFolder.id,
      version: currentSalesFolder.version,
      dirtyType: currentSalesFolder.dirtyType,
      memo: currentSalesFolder.memo,
    } : void 0,
    version: state.version,
  };
};
export const queryLeadMasterInfo = (state: LeadDetailsState) => state.masterInfo;
export const queryLeadIsUnsubscribed = (state: LeadDetailsState) => state.isUnsubscribed;
export const queryLeadMasterInfoHasErrors = ({ masterInfo }: LeadDetailsState) =>
  isNotNil(validateLeadMasterInfo(getFormStateSnapshotValue(masterInfo)));
export const queryLeadCurrentSalesFolderHasErrors = ({
                                                       currentSalesFolder,
                                                       currentSalesFolderInEditing,
                                                     }: LeadDetailsState) =>
  isNotNil(currentSalesFolder) && currentSalesFolderInEditing
  && isNotNil(validatesLeadSalesFolder(getFormStateSnapshotValue(currentSalesFolder.general)));
export const queryLeadHasErrors = (state: LeadDetailsState) =>
  queryLeadMasterInfoHasErrors(state) || queryLeadCurrentSalesFolderHasErrors(state);
export const queryCommunications = (state: LeadDetailsState) => state.communications;
export const queryCommunicationListFilters = (state: LeadDetailsState) => state.communicationListFilters;
export const queryCommunicationListSmsFilter = (state: LeadDetailsState) => state.communicationListFilters.showSms;
export const queryCommunicationListDocFilter = (state: LeadDetailsState) => state.communicationListFilters.showDoc;
export const queryCommunicationListActivityFilter = (state: LeadDetailsState) => state.communicationListFilters.showActivity;
export const queryCommunicationListEmailFilter = (state: LeadDetailsState) => state.communicationListFilters.showEmail;
export const queryCommunicationListWebsiteFilter = (state: LeadDetailsState) =>
  state.communicationListFilters.showWebsite;
export const queryLeadGDriveUrl = (state: LeadDetailsState) => state.gDriveUrl;
export const queryLeadGDriveFolderId = (state: LeadDetailsState) => state.gDriveFolderId;
export const queryLeadEmail = (state: LeadDetailsState) => state.email;
// tslint:disable-next-line:no-non-null-assertion
export const queryLeadPhone = (state: LeadDetailsState) => queryLeadContact(state)!.phone;
// tslint:disable-next-line:no-non-null-assertion
export const queryCurrentSalesFolderId = ({ currentSalesFolder }: LeadDetailsState) =>
  currentSalesFolder ? currentSalesFolder.id : void 0;
export const querySalesPersonId = (state: LeadDetailsState) => state.masterInfo?.value?.salesPerson?.userId;
